import { Link } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { proCourses, firstSentence, shortSalary } from '@/content/proCourses';
import { RangeBars } from '@/components/RangeBars';

/**
 * /careers/courses — hub listing the professional-credential deep dives.
 * Khadi & Indigo theme, consistent with CareersPage.
 */
export function CoursesHubPage() {
  return (
    <div>
      <SEO
        title="Professional Courses for Commerce — ACCA, CMA, CA & US CPA | ACES"
        description="Honest, India-focused deep dives into the top professional courses after commerce — ACCA, ACCA in India, CMA USA, CMA India, CA and US CPA. Eligibility, cost, salary, scope and career growth, side by side."
        canonical="https://acescommerce.com/careers/courses/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Professional courses for commerce students',
          description: 'Deep-dive guides to ACCA, CMA, CA and US CPA for Indian commerce students.',
          url: 'https://acescommerce.com/careers/courses/',
        }}
      />

      {/* Hero */}
      <section className="aces-hero">
        <div className="max-w-5xl mx-auto px-4 pt-14 sm:pt-16 pb-10">
          <nav className="text-xs text-ink-500 mb-6">
            <Link to="/" className="hover:text-accent-600">Home</Link>
            <span className="text-ink-300 mx-1.5">·</span>
            <Link to="/careers/" className="hover:text-accent-600">Careers</Link>
            <span className="text-ink-300 mx-1.5">·</span>
            <span className="text-ink-600">Professional courses</span>
          </nav>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-accent-600 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            Professional courses
          </span>
          <h1 className="font-serif text-[2.4rem] sm:text-5xl lg:text-[3.4rem] text-ink-900 tracking-tight leading-[1.06] max-w-2xl text-balance">
            The big finance &amp; accounting <em className="italic text-accent-600">credentials</em>, decoded
          </h1>
          <p className="mt-5 text-lg text-ink-600 max-w-2xl leading-relaxed">
            ACCA, CMA, CA, CPA — three-letter names, very different paths. Here's an honest,
            India-focused breakdown of each: who it suits, what it costs, what you'll actually
            earn, and where it can take you.
          </p>
        </div>
      </section>

      {/* Cards */}
      <section className="bg-ink-50">
        <div className="max-w-5xl mx-auto px-4 pb-16">
          {proCourses.length === 0 ? (
            <p className="text-ink-500 text-sm">Course guides are being prepared — check back shortly.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {proCourses.map((c) => (
                <Link
                  key={c.slug}
                  to={`/careers/courses/${c.slug}/`}
                  className="group aces-card-hover rounded-card border border-ink-200/80 bg-cream p-5 sm:p-6 shadow-card hover:border-accent-500/50"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <h2 className="font-serif text-[1.4rem] text-ink-900 tracking-tight transition-colors duration-150 group-hover:text-accent-700">{c.name}</h2>
                    <span className="shrink-0 text-ink-500 group-hover:text-accent-600 transition-colors text-sm">Full guide →</span>
                  </div>
                  <p className="text-xs text-ink-500 mt-0.5">{c.fullName}</p>
                  <p className="text-sm text-ink-700 mt-3 leading-snug">{c.oneLiner}</p>
                  <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
                    <span className="aces-pill">{firstSentence(c.duration)}</span>
                    <span className="px-2.5 py-0.5 rounded-full bg-accent-50 text-accent-700 font-medium border border-accent-200/70">Start: {shortSalary(c.startingSalaryIndia)}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Starting-salary comparison */}
      {proCourses.length > 0 && proCourses.every((c) => c.salaryProgression?.length) && (
        <section className="bg-cream border-t border-ink-200">
          <div className="max-w-3xl mx-auto px-4 py-14">
            <h2 className="font-serif text-[1.75rem] text-ink-900 tracking-tight mb-2">Starting salary, compared</h2>
            <p className="text-ink-500 text-sm mb-5">
              Realistic fresher pay in India (₹ lakh per annum). CA and US CPA tend to start higher;
              ACCA and CMA usually start lower.
            </p>
            <RangeBars
              rows={proCourses.map((c) => ({ label: c.name, low: c.salaryProgression?.[0]?.low ?? 0, high: c.salaryProgression?.[0]?.high ?? 0 }))}
              ariaLabel="Starting salary comparison across the six courses in India"
            />
          </div>
        </section>
      )}
    </div>
  );
}
